import { Component, JSX, Show } from "solid-js";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
  type?: "button" | "submit" | "reset";
  class?: string;
  ariaLabel?: string;
  onClick?: () => void;
  children: JSX.Element;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-ink text-paper hover:bg-ink-2",
  secondary:
    "bg-surface text-ink border border-line hover:bg-sunken hover:border-line-strong",
  ghost: "bg-transparent text-ink-2 hover:bg-sunken hover:text-ink",
  danger:
    "bg-transparent text-signal-red border border-signal-red hover:bg-signal-red-tint",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-7 px-2.5 text-xs",
  md: "h-7 px-3 text-sm",
  lg: "h-7 px-4 text-sm",
};

const spinnerSizes: Record<ButtonSize, "sm" | "md" | "lg"> = {
  sm: "sm",
  md: "sm",
  lg: "md",
};

export const Button: Component<ButtonProps> = (props) => {
  const isDisabled = () => props.disabled || props.loading;
  const variant = () => props.variant ?? "primary";
  const size = () => props.size ?? "md";

  return (
    <button
      type={props.type ?? "button"}
      class={
        "inline-flex items-center justify-center gap-1.5 font-medium rounded transition-colors focus-ring " +
        variantClasses[variant()] +
        " " +
        sizeClasses[size()] +
        " " +
        (isDisabled() ? "opacity-50 cursor-not-allowed" : "") +
        " " +
        (props.class ?? "")
      }
      disabled={isDisabled()}
      aria-disabled={isDisabled()}
      aria-busy={props.loading}
      aria-label={props.ariaLabel}
      onClick={() => !isDisabled() && props.onClick?.()}
    >
      <Show when={props.loading}>
        <Spinner size={spinnerSizes[size()]} />
      </Show>
      <Show when={!props.loading && props.leftIcon}>{props.leftIcon}</Show>
      {props.children}
      <Show when={!props.loading && props.rightIcon}>{props.rightIcon}</Show>
    </button>
  );
};
